// Création d'un îlot (D-300) : quatre coins cliqués sur le fond OpenStreetMap dans l'ordre du contour
// (façade de la rangée 1 de A vers B), rangées, places par rangée, dimensions types et règle de
// numérotation ; le serveur découpe l'îlot en places. Les dimensions mesurées sur la carte proposent
// un comptage, que l'agent corrige ensuite sur le terrain.
import {
  dimensionsIlot,
  referencePlace,
  verifierQuadrilatere,
  type Coord,
  type Numerotation,
} from '@cc/contrats';
import { Bandeau, Bouton, Champ, Selection } from '@cc/ui';
import { useMemo, useState } from 'react';
import { useAction } from '../../commun/useAction';
import { useRubriques } from '../recettes/requetes';
import { INVALIDER_MARCHES } from './api';
import { creerIlot } from './plan-api';
import styles from './marches.module.css';

const nombre = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

export function EditeurIlot({
  marcheId,
  coins,
  onTermine,
}: {
  marcheId: string;
  coins: Coord[];
  onTermine: () => void;
}) {
  const dims = useMemo(
    () => (coins.length === 4 && !verifierQuadrilatere(coins) ? dimensionsIlot(coins) : null),
    [coins],
  );
  const [nom, setNom] = useState('');
  const [code, setCode] = useState('');
  const [largeur, setLargeur] = useState(2);
  const [profondeur, setProfondeur] = useState(2);
  const [rangees, setRangees] = useState<number | ''>('');
  const [parRangee, setParRangee] = useState<number | ''>('');
  const [regle, setRegle] = useState<Numerotation>({
    format: 'ilot_rangee_rang',
    sens: 'meme_sens',
    premier_numero: 1,
    chiffres: 3,
  });
  const [rubrique, setRubrique] = useState('');
  const rubriques = useRubriques();
  const propose = dims
    ? {
        rangees: Math.max(1, Math.round(dims.profondeur_m / profondeur)),
        places: Math.max(1, Math.round(dims.facade_m / largeur)),
      }
    : null;
  const r = rangees === '' ? (propose?.rangees ?? 1) : rangees;
  const n = parRangee === '' ? (propose?.places ?? 1) : parRangee;
  const action = useAction({
    executer: () =>
      creerIlot(marcheId, {
        nom,
        ...(code.trim() ? { code: code.trim() } : {}),
        coins,
        rangees: r,
        places_par_rangee: n,
        largeur_type_m: largeur,
        profondeur_type_m: profondeur,
        numerotation: regle,
        rubrique_code: rubrique,
      }),
    succes: (x) => `Îlot ${x.code} créé et découpé en ${x.places} places.`,
    invalider: INVALIDER_MARCHES,
    apres: onTermine,
  });
  if (coins.length < 4)
    return (
      <p>
        Cliquez sur la carte les quatre coins de l’îlot, dans l’ordre du contour, en commençant par
        le début de la façade ({coins.length} sur 4).
      </p>
    );
  const probleme = verifierQuadrilatere(coins);
  return (
    <form
      className={styles.pile}
      aria-label="Découper un îlot"
      onSubmit={(e) => {
        e.preventDefault();
        action.lancer(undefined);
      }}
    >
      {probleme ? <Bandeau type="erreur">{probleme}. Recommencez le contour.</Bandeau> : null}
      {dims ? (
        <p className={styles.aide}>
          Façade mesurée sur la carte : {nombre(dims.facade_m)} m ; profondeur :{' '}
          {nombre(dims.profondeur_m)} m. Comptage proposé : {propose!.rangees} rangées de{' '}
          {propose!.places} places.
        </p>
      ) : null}
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Nom de l’îlot"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          maxLength={120}
          required
        />
        <Champ
          libelle="Code (facultatif)"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={10}
          pattern="[A-Za-z0-9]{1,10}"
        />
        <Champ
          libelle="Largeur type d’une place (m)"
          type="number"
          min={0.5}
          max={50}
          step={0.1}
          value={largeur}
          onChange={(e) => setLargeur(Number(e.target.value))}
        />
        <Champ
          libelle="Profondeur type (m)"
          type="number"
          min={0.5}
          max={50}
          step={0.1}
          value={profondeur}
          onChange={(e) => setProfondeur(Number(e.target.value))}
        />
        <Champ
          libelle="Rangées"
          type="number"
          min={1}
          max={200}
          value={r}
          onChange={(e) => setRangees(e.target.value === '' ? '' : Number(e.target.value))}
          required
        />
        <Champ
          libelle="Places par rangée"
          type="number"
          min={1}
          max={500}
          value={n}
          onChange={(e) => setParRangee(e.target.value === '' ? '' : Number(e.target.value))}
          required
        />
      </div>
      <div className={styles.barreFiltres}>
        <Selection
          libelle="Numérotation"
          value={regle.format}
          onChange={(e) => setRegle({ ...regle, format: e.target.value as Numerotation['format'] })}
          options={[
            { valeur: 'ilot_rangee_rang', libelle: 'Îlot, rangée, rang' },
            { valeur: 'ilot_continu', libelle: 'Îlot puis numéro continu' },
          ]}
        />
        <Selection
          libelle="Sens"
          value={regle.sens}
          onChange={(e) => setRegle({ ...regle, sens: e.target.value as Numerotation['sens'] })}
          options={[
            { valeur: 'meme_sens', libelle: 'Toutes les rangées dans le même sens' },
            { valeur: 'alterne', libelle: 'En serpentin (rangées paires à rebours)' },
          ]}
        />
        <Champ
          libelle="Premier numéro"
          type="number"
          min={0}
          max={9999}
          value={regle.premier_numero}
          onChange={(e) => setRegle({ ...regle, premier_numero: Number(e.target.value) })}
        />
        <Selection
          libelle="Redevance au forfait mensuel"
          value={rubrique}
          required
          optionVide="Choisir la rubrique"
          onChange={(e) => setRubrique(e.target.value)}
          options={(rubriques.data ?? [])
            .filter((x) => x.active && x.base_calcul === 'forfait' && x.periodicite === 'mensuelle')
            .map((x) => ({ valeur: x.code, libelle: x.libelle }))}
        />
      </div>
      <p className={styles.aide}>
        {r * n} places, de {referencePlace(code.trim() || 'X', 1, 1, n, regle)} à{' '}
        {referencePlace(code.trim() || 'X', r, n, n, regle)}
        {code.trim() ? '' : ' (X : code attribué à la création)'}. Placement schématique à relever
        sur le terrain ; aucun loyer n’est fixé par cet outil.
      </p>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      <Bouton
        type="submit"
        disabled={Boolean(probleme) || !nom.trim() || !rubrique || action.enCours}
      >
        Créer et découper l’îlot
      </Bouton>
    </form>
  );
}
